import { Loader2, Search, X } from "lucide-react";

import { cn } from "@/utils/cn";

/**
 * The base Search Input primitive from docs/ui/design-system.md §11 —
 * icon-prefixed field, clear affordance once there's a query, an optional
 * loading state for in-flight results. This is the shared shell; the
 * "smart"/AI-assisted variant and result-count announcements
 * (component-inventory.md `SearchBar`) are feature-level composition on top
 * of this once product confirms that variant ships (§22 of
 * ui-architecture.md flags it as scope-TBD) — not built here.
 */
export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function SearchInput({
  value,
  onChange,
  onSubmit,
  placeholder = "Search",
  isLoading = false,
  className,
  "aria-label": ariaLabel = "Search",
}: SearchInputProps) {
  return (
    <div className={cn("relative flex h-10 items-center", className)}>
      <Search
        className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type="search"
        role="searchbox"
        aria-label={ariaLabel}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            onSubmit?.(value);
          }
        }}
        className="h-10 w-full rounded-md border border-input bg-transparent py-1 pl-9 pr-9 text-body placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
      <div className="absolute right-3 flex items-center">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : (
          value.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => onChange("")}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )
        )}
      </div>
    </div>
  );
}
